import React, { useEffect, useState } from 'react';

import { trackDemoOpen, trackDemoResetNoUser } from '../../analytics';

import { PageWrapper } from '../../components/layout/content';
import { ErrorModal } from '../../components/modals';

import useDemoData from '../../hooks/useDemoData';

import { getOneCreation } from '../../services/api/creations';
import { getEventPublic } from '../../services/api/events';
import { getFaceById } from '../../services/api/faces';
import { getReviewData } from '../../services/videoBuilderService';

import {
  PublicEvent,
  Face,
  PageError,
  ModalError,
  DemoData,
} from '../../types';
import { isProduction, isEmpty } from '../../utils';

import DemoStepper from './components/demoStepper';
import ExistingEmailModal from './components/existingEmailModal';
import AnalyseStep from './steps/analyse';
import GenerateStep from './steps/generate';
import IntroStep from './steps/intro';
import UploadStep from './steps/upload';

const PROD_EVENT_ID = 'e982d18a-038f-46b2-8bac-f1393b2eea26';
const STG_EVENT_ID = 'f378cd70-d88d-446c-ab76-e952bb9e12b2';

const Demo = () => {
  const [event, setEvent] = useState<PublicEvent | null>(null);

  const { demoData, updateDemoData, resetDemoData } = useDemoData();

  const [step, setStep] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  const [showExistUserModal, setShowExistUserModal] = useState(false);
  const [existUserData, setExistUserData] = useState<DemoData | null>(null);

  const eventId = isProduction() ? PROD_EVENT_ID : STG_EVENT_ID;

  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      const fetchedEvent = await getEventPublic(eventId);

      if (!fetchedEvent) {
        setPageError(404);
        return;
      }

      // check if user exists in the system, if not – reset demo data and reload
      if (demoData.user) {
        const face = await getFaceById(demoData.user.elementId, true);
        if (!face) {
          resetDemoData();
          window.location.reload();
          trackDemoResetNoUser();
          return;
        }
      }

      switch (demoData.status) {
        case 'generation':
          setStep(3);
          break;
        case 'analysis':
          setStep(2);
          break;
        case 'upload':
          setStep(1);
          break;
        default:
          break;
      }

      setEvent(fetchedEvent);
      setIsLoading(false);

      trackDemoOpen(demoData.status);
    };

    onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevStep = () => {
    setStep((prev) => prev - 1);
    setError(null);
  };

  const nextStep = () => {
    setStep((prev) => prev + 1);
    setError(null);
  };

  const handleExistingUser = async (face: Face) => {
    const defaultData: DemoData = {
      status: 'upload',
      user: { email: face.email, elementId: face.elementId },
      photos: [],
      clips: [],
      notifications: { analysis: true, generation: true },
    };

    // if creation is generated – suggest going to generation step
    const creation = await getOneCreation(face.elementId);

    if (creation) {
      setShowExistUserModal(true);
      setExistUserData({ ...defaultData, status: 'generation' });

      return;
    }

    // if analysis started – suggest going to analysis step
    const { people } = await getReviewData({ eventId, isPublic: true });

    const foundFace = people?.find(
      (person) => person.face.elementId === face.elementId,
    );

    if (foundFace && !isEmpty(foundFace.files)) {
      setShowExistUserModal(true);
      setExistUserData({
        ...defaultData,
        status: 'analysis',
        photos: foundFace.files?.map((f) => f.elementId) ?? [],
        clips: foundFace.allClips?.map((c) => c.parentElementId ?? '') ?? [],
      });

      return;
    }

    // else go to upload step
    updateDemoData(defaultData);
    nextStep();
  };

  const getContent = () => {
    if (!event || !!error) return <></>;

    switch (step) {
      case 0:
      default:
        return (
          <IntroStep
            event={event}
            nextStep={nextStep}
            handleExistingUser={handleExistingUser}
            setError={setError}
          />
        );
      case 1:
        return (
          <UploadStep event={event} nextStep={nextStep} setError={setError} />
        );
      case 2:
        return (
          <AnalyseStep
            event={event}
            prevStep={prevStep}
            nextStep={nextStep}
            setError={setError}
          />
        );
      case 3:
        return <GenerateStep event={event} setError={setError} />;
    }
  };

  return (
    <PageWrapper
      type="header-only"
      title="Demo"
      isLoading={isLoading}
      pageError={pageError}
      className="items-center gap-4"
    >
      {step !== 0 && <DemoStepper activeStep={step - 1} />}

      <div className="w-full h-full xl:w-4/5 flex flex-col self-center items-center text-center gap-4">
        {getContent()}
      </div>

      {existUserData && (
        <ExistingEmailModal
          show={showExistUserModal}
          onHide={() => setShowExistUserModal(false)}
          userData={existUserData}
          setStep={setStep}
        />
      )}

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        btn={error?.btn}
        isTryAgainBtn
        isContactUs
      />
    </PageWrapper>
  );
};

export default Demo;
